import type { PlanDefinition, PlanDraft } from "@weights/plan-reading";
import { labelKey, looksLikeLabel } from "./lib.js";

import { normalizeDraft as normalizeShared } from "@weights/plan-reading";
import { readingOf } from "./reading.js";

export { blankDraft, copyWeek, newColumnID, nextTitle } from "@weights/plan-reading";

/** The shared normalizer, with each definition's reading kept (§61.2) whether or not it knows the field. */
export function normalizeDraft(draft: PlanDraft, week: string): PlanDraft {
  const out = normalizeShared(draft, week);
  const byLabel = new Map((draft.sessions ?? []).map((d) => [d.label, readingOf(d)]));
  if (!out.sessions.some((d) => !readingOf(d) && byLabel.get(d.label))) return out;
  return { ...out, sessions: out.sessions.map((d) => (readingOf(d) || !byLabel.get(d.label) ? d : ({ ...d, reading: structuredClone(byLabel.get(d.label)) } as PlanDefinition))) };
}

export function cellOf(draft: PlanDraft, r: number, c: number): string[] {
  const column = draft.columns[c];
  if (!column) return [];
  return draft.days[r]?.cells[column.id] ?? [];
}

export function definitionIndex(draft: PlanDraft): Map<string, number> {
  const index = new Map<string, number>();
  draft.sessions.forEach((s, i) => {
    const key = labelKey(s.label);
    if (key && !index.has(key)) index.set(key, i);
  });
  return index;
}

export function findDefinition(draft: PlanDraft, label: string): PlanDefinition | undefined {
  const key = labelKey(label);
  return draft.sessions.find((s) => labelKey(s.label) === key);
}

/** A chip names a session the week has not defined. Free text never does. */
export function isUndefinedLabel(chip: string, defined: Set<string>): boolean {
  return looksLikeLabel(chip) && !defined.has(labelKey(chip));
}

export function definedKeys(draft: PlanDraft): Set<string> {
  return new Set(draft.sessions.map((s) => labelKey(s.label)).filter(Boolean));
}

/** Every label-shaped chip of the week, first appearance order, as written. */
export function labelsInCells(draft: PlanDraft): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const day of draft.days)
    for (const column of draft.columns)
      for (const chip of day.cells[column.id] ?? []) {
        if (!looksLikeLabel(chip)) continue;
        const key = labelKey(chip);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(chip);
      }
  return out;
}

export function isEmptyWeek(draft: PlanDraft): boolean {
  return draft.days.every((d) => Object.values(d.cells).every((c) => c.length === 0)) && draft.sessions.length === 0;
}

export function sameChips(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  const x = a ?? [];
  const y = b ?? [];
  return x.length === y.length && x.every((v, i) => v === y[i]);
}
